import { useSearchParams } from '@remix-run/react';
import { useEffect, useMemo } from 'react';
import { z } from 'zod';
import { useApi } from '../providers/api-provider';
import { useUserSettingsStore } from '../stores/user-settings-store';

// The name of the search query parameter with a referral code.
const REFERRAL_CODE_PARAMETER = 'referral_code';

// The referral url is constructed from the base url and this path.
const REFERRAL_PATH = '/';

export function extractReferralCode(url: string) {
  try {
    const urlData = new URL(url);
    const referralCode = urlData.searchParams.get(REFERRAL_CODE_PARAMETER);
    return referralCode;
  } catch (error) {
    console.error('Invalid request URL', error);
    return null;
  }
}

const referralCodeSchema = z.string().min(1);

function validateReferralCode(referralCode: string): boolean {
  return referralCodeSchema.safeParse(referralCode).success;
}

export const useReferralCode = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { setReferralCode } = useUserSettingsStore();

  useEffect(() => {
    const referralCode = searchParams.get(REFERRAL_CODE_PARAMETER);
    if (referralCode && validateReferralCode(referralCode)) {
      setReferralCode(referralCode);

      // Remove referral code from the url since it's already stored in the
      // local storage.
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete(REFERRAL_CODE_PARAMETER);
      setSearchParams(newSearchParams);
    }
  }, [setReferralCode, searchParams, setSearchParams]);
};

export const useReferralUrl = () => {
  const { api } = useApi();
  const { data: data } = api.useGetProfile();

  const referralUrl = useMemo(() => {
    if (!data?.referral_code) return null;
    const referralUrl = new URL(REFERRAL_PATH, window.location.origin);
    referralUrl.searchParams.set(REFERRAL_CODE_PARAMETER, data!.referral_code);
    return referralUrl.toString();
  }, [data]);

  return referralUrl;
};
